<template>
  <div ref="containerRef" class="@container">
    <div
      class="grid grid-cols-[1fr_auto] gap-3 text-navy @xl:grid-cols-[minmax(min-content,220px)_1fr_auto] @xl:items-center @xl:gap-6"
    >
      <div class="flex items-center gap-3 @xl:order-1 @xl:shrink">
        <Box>
          <ProfileImpactImage
            :type="type === 'family' ? type : getCampaignOfImpactType(type)"
          />
        </Box>
        <div class="flex-1 space-y-1">
          <div
            class="font-Kontesa text-[21px] font-bold leading-none tracking-tight"
            :class="
              previousAmount !== undefined &&
              previousAmount !== amount &&
              currentAmount === amount &&
              'grow-shrink'
            "
          >
            {{ numberFormatter.format(currentAmount) }}
          </div>
          <div
            class="font-Aeonik text-[15px] font-normal leading-none tracking-tight"
          >
            {{ unitOfImpact }}
          </div>
        </div>
      </div>
      <div class="flex items-center gap-3 @xl:order-3 @xl:shrink">
        <div
          class="font-Aeonik text-[15px] font-normal leading-none tracking-tight"
          :class="{
            'grow-shrink':
              initial.level < final.level && currentLevel === final.level,
          }"
        >
          {{ numberFormatter.format(toNextLevel) }}
        </div>
        <slot />
      </div>
      <ProfileImpactProgressBar
        class="col-span-full @xl:order-2 @xl:col-span-1"
        :progress="currentProgress"
        :level="currentLevel"
      >
        <template v-if="type === 'family'" #icon>
          <IconsPersonCharging class="aspect-square h-[60%] text-white" />
        </template>
      </ProfileImpactProgressBar>
    </div>

    <DialogRoot
      v-model:open="isModalOpen"
      @update:open="(v) => !v && onAnimationEnd()"
    >
      <ProfileDialogContent class="w-full max-w-[375px]" title="WOOHOO!">
        <img
          class="mx-auto h-16 w-16"
          :src="
            type !== 'family'
              ? emojis[getCampaignOfImpactType(type)]
              : '/images/icons/avatar.svg'
          "
        />

        <p
          class="mt-3 text-balance text-center font-Kontesa text-3xl font-bold"
        >
          {{ numberFormatter.format(amount - (previousAmount ?? 0)) }} more
          {{ unitOfImpact
          }}{{
            unitOfImpact === "Members" ? " have joined your impact family" : ""
          }}!
        </p>

        <p
          v-if="animationCount === 0 && previousAmount !== 0"
          class="mt-3 text-center text-sm opacity-70"
        >
          Since last login
        </p>

        <p v-if="type === 'seagrass'" class="mt-3 text-center font-medium">
          As a member, you can propose and vote on names for this robot!
        </p>

        <Button
          v-if="type === 'seagrass'"
          class="mt-5 w-full"
          variant="primary-flat"
          @click="onSubmitRobotNameClick"
        >
          Submit a robot name!
        </Button>
        <Button
          v-else
          class="mt-5 w-full"
          variant="primary-flat"
          @click="
            onAnimationEnd();
            isModalOpen = false;
          "
        >
          Continue
        </Button>
      </ProfileDialogContent>
    </DialogRoot>
  </div>
</template>

<script setup lang="ts">
import { useBodyScrollLock } from "radix-vue";
import { Campaign, emojis } from "~/lib/campaigns";
import { scrollIntoView } from "~/lib/dom";
import { getLevelInfo } from "~/lib/levels";

type ImpactType = "trees" | "ocean" | "education" | "seagrass" | "family";
const props = defineProps<{
  type: ImpactType;
  amount: number;
  previousAmount?: number;
}>();

const emit = defineEmits<{
  (e: "animation-end"): void;
}>();

const type = toRef(props, "type");
const unitOfImpact = computed(() => {
  switch (type.value) {
    case "trees":
      return "Trees Planted";
    case "ocean":
      return "Lbs of Plastic Removed";
    case "education":
      return "Days of School Funded";
    case "seagrass":
      return "Seagrass Seeds Planted";
    case "family":
      return "Members";
    default:
      return "";
  }
});

function getCampaignOfImpactType(impact: Exclude<ImpactType, "family">) {
  switch (impact) {
    case "trees":
      return Campaign.TFTF;
    case "ocean":
      return Campaign.PF;
    case "education":
      return Campaign.SARTHAK;
    case "seagrass":
      return Campaign.REEFGEN;
  }
}

const SPEED = 100 / 1500; // %/ms

const previousAmount = toRef(props, "previousAmount");
const amount = toRef(props, "amount");

const initial = computed(() =>
  getLevelInfo(previousAmount.value ?? amount.value),
);
const final = computed(() => getLevelInfo(amount.value));

const currentLevel = ref(
  (previousAmount.value ?? 0) > amount.value
    ? final.value.level
    : initial.value.level,
);
const currentProgress = ref(
  (previousAmount.value ?? 0) > amount.value
    ? final.value.progress
    : initial.value.progress,
);
const initialProgress = ref(currentProgress.value);

const currentAmount = ref(
  (previousAmount.value ?? 0) > amount.value
    ? amount.value
    : (previousAmount.value ?? amount.value),
);

const toNextLevel = computed(() => {
  return currentLevel.value === initial.value.level
    ? initial.value.totalExpToNextLevel
    : final.value.totalExpToNextLevel;
});

const duration = computed(() =>
  Math.max(
    800,
    ((final.value.level > currentLevel.value ? 100 : final.value.progress) -
      initialProgress.value) /
      SPEED,
  ),
);
let currentTime = 0;

const raf = useRafFn(
  ({ delta }) => {
    if (initial.value.level > final.value.level) {
      emit("animation-end");
      return;
    }

    const animateLevel = final.value.level > currentLevel.value;
    const targetProgress = animateLevel ? 100 : final.value.progress;
    currentTime += delta;
    const ease = 1 - Math.pow(1 - currentTime / duration.value, 3);
    currentProgress.value = Math.min(
      targetProgress,
      (targetProgress - initialProgress.value) * ease + initialProgress.value,
    );
    if (currentProgress.value === targetProgress) {
      currentTime = 0;
      raf.pause();
      if (animateLevel) {
        nextTick(() => {
          currentLevel.value = final.value.level;
          currentProgress.value = 0;
          initialProgress.value = 0;
          raf.resume();
          playProgressSound();
        });
      } else if (
        previousAmount.value !== undefined &&
        previousAmount.value !== amount.value
      )
        isModalOpen.value = true;
      else {
        onAnimationEnd();
      }
    }
  },
  { immediate: false },
);

const containerRef = ref<HTMLElement | null>(null);

const scrollLock = useBodyScrollLock();
async function playAnimation() {
  currentAmount.value = amount.value;
  initialProgress.value = currentProgress.value;
  if (
    containerRef.value &&
    previousAmount.value !== undefined &&
    previousAmount.value !== amount.value
  ) {
    scrollLock.value = true;
    await nextTick();
    await scrollIntoView(containerRef.value, {
      behavior: "smooth",
      block: "center",
    });
    // Wait a few ms after scroll
    await new Promise((resolve) => setTimeout(resolve, 500));
    playProgressSound();
  }
  raf.resume();
}

const animationCount = ref(0);

async function onAnimationEnd() {
  animationCount.value++;
  scrollLock.value = false;
  await nextTick();
  emit("animation-end");
}

function playProgressSound() {
  const audio = new Audio("/sounds/progress-bar.mp3");
  audio.play();
}

function playPopupSound() {
  const audio = new Audio("/sounds/pop-up.mp3");
  audio.play();
}

const isModalOpen = ref(false);
watchEffect(() => {
  if (isModalOpen.value) {
    playPopupSound();
  }
});

function onSubmitRobotNameClick() {
  isModalOpen.value = false;
  navigateTo({ path: "/campaigns/reefgen", hash: "#robotNameForm" });
}

defineExpose({ playAnimation });

const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  notation: "compact",
  maximumFractionDigits: 2,
});
</script>

<style scoped>
.grow-shrink {
  animation: grow-shrink 600ms;
}

@keyframes grow-shrink {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
